import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // Server-side rendering fallback
    if (typeof window === 'undefined') return 'light';
    
    // Try to get saved theme from localStorage
    try {
      const saved = localStorage.getItem('theme');
      if (saved) return saved;
    } catch (error) {
      console.warn('Failed to access localStorage:', error);
    }

    // Fallback to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    // Debugging log (optional, remove in production)
    console.log('Applying theme:', theme);
    
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Save theme to localStorage
    try {
      localStorage.setItem('theme', theme);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [theme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      try {
        const saved = localStorage.getItem('theme');
        if (!saved) {
          const newTheme = mediaQuery.matches ? 'dark' : 'light';
          setTheme(newTheme);
          console.log('System preference changed to:', newTheme);
        }
      } catch (error) {
        console.warn('Failed to check localStorage on system preference change:', error);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}